import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { DAY_NAMES } from '../utils/dayBorn';
import dayBorns from '../data/akanDayBorns.json';
import useSEO from '../hooks/useSEO';
import { countryOptions } from '../utils/country';

const COUNTRY_OPTIONS = countryOptions();

const GOLD = '#C9A558';

// ─── Small building blocks ────────────────────────────────────────────────────

function Panel({ title, subtitle, children, style }) {
  return (
    <section className="afia-card" style={{ padding: '28px 26px', ...style }}>
      <h2 style={{
        fontFamily: "'Cinzel', serif", fontSize: 14, color: GOLD,
        letterSpacing: '0.14em', textTransform: 'uppercase', margin: 0,
      }}>
        {title}
      </h2>
      {subtitle && (
        <p style={{ color: '#A8856A', fontSize: 13, lineHeight: 1.6, margin: '8px 0 0' }}>
          {subtitle}
        </p>
      )}
      <div style={{ marginTop: 22 }}>{children}</div>
    </section>
  );
}

function Field({ id, label, hint, children }) {
  return (
    <div className="form-field">
      <label className="field-label" htmlFor={id}>{label}</label>
      {children}
      {hint && (
        <p style={{ color: '#A8856A', fontSize: 11.5, margin: '7px 0 0', lineHeight: 1.5 }}>{hint}</p>
      )}
    </div>
  );
}

function PwField({ id, label, value, onChange }) {
  return (
    <Field id={id} label={label}>
      <input
        id={id}
        type="password"
        value={value}
        onChange={onChange}
        className="afia-input-plain"
        style={{
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Verdana, sans-serif',
          letterSpacing: '0.18em', color: '#EDD9BC',
        }}
        placeholder="••••••••"
        required
      />
    </Field>
  );
}

/** A row of the "at a glance" strip. */
function Stat({ label, value }) {
  return (
    <div style={{ flex: '1 1 120px', minWidth: 0 }}>
      <div style={{
        fontFamily: "'Montserrat', sans-serif", fontSize: 10,
        letterSpacing: '0.16em', textTransform: 'uppercase', color: '#9E7D42', marginBottom: 6,
      }}>
        {label}
      </div>
      <div style={{
        fontFamily: "'Cinzel', serif", fontSize: 15, color: '#EDD9BC',
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        {value}
      </div>
    </div>
  );
}

function QuickLink({ to, title, blurb }) {
  return (
    <Link
      to={to}
      style={{
        display: 'block', textDecoration: 'none',
        border: '1px solid rgba(201,165,88,0.22)', borderRadius: 10,
        padding: '14px 16px', background: 'rgba(201,165,88,0.04)',
        transition: 'border-color .2s, background .2s',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = 'rgba(201,165,88,0.55)';
        e.currentTarget.style.background = 'rgba(201,165,88,0.09)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = 'rgba(201,165,88,0.22)';
        e.currentTarget.style.background = 'rgba(201,165,88,0.04)';
      }}
    >
      <div style={{ fontFamily: "'Cinzel', serif", fontSize: 13.5, color: GOLD, letterSpacing: '0.06em' }}>
        {title}
      </div>
      <div style={{ color: '#A8856A', fontSize: 12.5, marginTop: 4, lineHeight: 1.5 }}>{blurb}</div>
    </Link>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function AccountPage() {
  const { user, authLoading, logout, updateProfile, changePassword } = useAuth();
  const navigate = useNavigate();

  useSEO({ title: 'Your Profile — Mama Africa Official', description: 'Your Mama Africa account.' });

  const [details, setDetails] = useState({ fullName: '', akanName: '', dayBorn: '', dob: '', country: '', phone: '', marketingOptIn: false });
  const [detailsMsg, setDetailsMsg] = useState({ error: '', success: '' });
  const [savingDetails, setSavingDetails] = useState(false);

  const [pw, setPw] = useState({ current: '', newPw: '', confirm: '' });
  const [pwMsg, setPwMsg] = useState({ error: '', success: '' });
  const [savingPw, setSavingPw] = useState(false);

  // Redirecting has to wait for the token check to finish. Reading `user` while authLoading is
  // still true says "signed out" for everyone, so refreshing this page used to bounce a
  // perfectly signed-in person to the sign-in screen — which is what made the session look
  // broken. Also: navigating during render is not allowed, hence the effect.
  useEffect(() => {
    if (!authLoading && !user) navigate('/auth', { replace: true });
  }, [authLoading, user, navigate]);

  useEffect(() => {
    if (user) {
      setDetails({
        fullName: user.fullName || user.name || '',
        akanName: user.akanName || '',
        dayBorn: user.dayBorn || '',
        dob: user.dob || '',
        country: user.country || '',
        phone: user.phone || '',
        marketingOptIn: Boolean(user.marketingOptIn),
      });
    }
  }, [user]);

  const day = user?.dayBorn && dayBorns[user.dayBorn] ? dayBorns[user.dayBorn] : null;

  const memberSince = useMemo(() => {
    if (!user?.createdAt) return '—';
    return new Date(user.createdAt).toLocaleDateString(undefined, {
      year: 'numeric', month: 'long', day: 'numeric',
    });
  }, [user]);

  if (authLoading) {
    return (
      <div className="page-wrapper">
        <section style={{ padding: '120px 20px', textAlign: 'center', color: '#A8856A' }}>
          Loading your profile…
        </section>
      </div>
    );
  }
  if (!user) return null;

  async function saveDetails(e) {
    e.preventDefault();
    setDetailsMsg({ error: '', success: '' });
    if (!details.fullName.trim()) {
      setDetailsMsg({ error: 'Please tell us what to call you.', success: '' });
      return;
    }
    setSavingDetails(true);
    const result = await updateProfile({
      fullName: details.fullName.trim(),
      akanName: details.akanName.trim(),
      dayBorn: details.dayBorn,
      dob: details.dob,
      // Empty strings clear the value; the API leaves fields it is not sent unchanged.
      country: details.country || '',
      phone: details.phone.trim(),
      marketingOptIn: details.marketingOptIn,
    });
    setSavingDetails(false);
    setDetailsMsg(result.success
      ? { error: '', success: 'Your details are saved.' }
      : { error: result.error, success: '' });
  }

  async function savePassword(e) {
    e.preventDefault();
    setPwMsg({ error: '', success: '' });
    // The API allows 8–100 characters. Nothing stricter is invented here — a rule the server
    // does not enforce only blocks people from passwords that would have worked.
    if (pw.newPw.length < 8) {
      setPwMsg({ error: 'Your new password needs at least 8 characters.', success: '' });
      return;
    }
    if (pw.newPw !== pw.confirm) {
      setPwMsg({ error: 'Both new password entries need to match.', success: '' });
      return;
    }
    setSavingPw(true);
    const result = await changePassword(pw.current, pw.newPw);
    setSavingPw(false);
    if (result.success) {
      setPwMsg({ error: '', success: 'Your password has been changed.' });
      setPw({ current: '', newPw: '', confirm: '' });
    } else {
      setPwMsg({ error: result.error, success: '' });
    }
  }

  const initials = (user.name || user.email || '?')
    .split(' ').filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join('');

  return (
    <div className="page-wrapper">
      <section style={{ padding: 'clamp(48px,7vw,84px) 20px 80px', maxWidth: 1060, margin: '0 auto' }}>

        {/* ── Header: who you are, at a glance ───────────────────────────── */}
        <div className="afia-card" style={{ padding: 'clamp(24px,4vw,36px)', marginBottom: 22 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 22, alignItems: 'center' }}>
            <div style={{
              width: 74, height: 74, borderRadius: '50%', flexShrink: 0,
              background: 'linear-gradient(135deg, rgba(201,165,88,0.22), rgba(201,165,88,0.06))',
              border: '1px solid rgba(201,165,88,0.45)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: "'Cinzel', serif", fontSize: 24, color: GOLD, letterSpacing: '0.04em',
            }}>
              {initials}
            </div>

            <div style={{ flex: '1 1 240px', minWidth: 0 }}>
              <h1 style={{
                fontFamily: "'Cinzel', serif", fontSize: 'clamp(21px,3.6vw,27px)',
                color: '#EDD9BC', letterSpacing: '0.05em', margin: 0,
              }}>
                {user.name}
              </h1>
              <p style={{ color: '#A8856A', fontSize: 14, margin: '6px 0 0', wordBreak: 'break-all' }}>
                {user.email}
              </p>
              {user.akanName && (
                <span className="status-badge status-delivered" style={{ marginTop: 10, display: 'inline-block' }}>
                  {user.akanName}{user.dayBorn ? ` · ${user.dayBorn}-born` : ''}
                </span>
              )}
            </div>

            <button
              className="btn-ghost"
              style={{ width: 'auto', padding: '10px 26px', flexShrink: 0 }}
              onClick={async () => { await logout(); navigate('/'); }}
            >
              Sign Out
            </button>
          </div>

          <div style={{
            display: 'flex', flexWrap: 'wrap', gap: 20,
            marginTop: 26, paddingTop: 22, borderTop: '1px solid rgba(201,165,88,0.16)',
          }}>
            <Stat label="Member since" value={memberSince} />
            <Stat label="Day born" value={user.dayBorn || 'Not set'} />
            <Stat label="Akan name" value={user.akanName || 'Not set'} />
            <Stat label="Birthday" value={user.dob || 'Not set'} />
          </div>
        </div>

        {/* ── Your day-born panel — only when we actually know the day ────── */}
        {day && (
          <div
            className="afia-card"
            style={{
              padding: 'clamp(22px,3.5vw,30px)', marginBottom: 22,
              display: 'flex', flexWrap: 'wrap', gap: 22, alignItems: 'flex-start',
            }}
          >
            <div style={{ fontSize: 40, lineHeight: 1, flexShrink: 0 }} aria-hidden="true">{day.symbol}</div>
            <div style={{ flex: '1 1 260px', minWidth: 0 }}>
              <div style={{
                fontFamily: "'Montserrat', sans-serif", fontSize: 10, letterSpacing: '0.16em',
                textTransform: 'uppercase', color: '#9E7D42', marginBottom: 8,
              }}>
                Your Kra din · soul name
              </div>
              <div style={{ fontFamily: "'Cinzel', serif", fontSize: 19, color: GOLD, letterSpacing: '0.06em' }}>
                {day.male} · {day.female}
              </div>
              <p style={{ color: '#D0AC88', fontSize: 13.5, lineHeight: 1.75, margin: '10px 0 0' }}>
                {day.meaning}
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 18, marginTop: 16 }}>
                <Stat label="Planet" value={day.planet} />
                <Stat label="Colour" value={day.color} />
              </div>
            </div>
          </div>
        )}

        {/* ── Two columns: details on the left, security on the right ─────── */}
        <div style={{
          display: 'grid', gap: 22,
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          alignItems: 'start',
        }}>
          <Panel
            title="Your details"
            subtitle="Afia greets you by this name, and your certificate is drawn from it."
          >
            <form onSubmit={saveDetails}>
              <Field id="acc-name" label="Full name">
                <input
                  id="acc-name" className="afia-input-plain" value={details.fullName}
                  onChange={(e) => setDetails((d) => ({ ...d, fullName: e.target.value }))}
                  autoComplete="name" required
                />
              </Field>

              <Field id="acc-dayborn" label="Day you were born"
                     hint="Sets your Akan name across the site.">
                <select
                  id="acc-dayborn" className="afia-input-plain" style={{ colorScheme: 'dark' }}
                  value={details.dayBorn}
                  onChange={(e) => {
                    const dayBorn = e.target.value;
                    setDetails((d) => ({ ...d, dayBorn }));
                  }}
                >
                  <option value="">Not set</option>
                  {DAY_NAMES.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </Field>

              <Field id="acc-akan" label="Akan name" hint="Leave blank to keep it unset.">
                <input
                  id="acc-akan" className="afia-input-plain" value={details.akanName}
                  onChange={(e) => setDetails((d) => ({ ...d, akanName: e.target.value }))}
                  placeholder={details.dayBorn && dayBorns[details.dayBorn]
                    ? `e.g. ${dayBorns[details.dayBorn].male} or ${dayBorns[details.dayBorn].female}`
                    : 'e.g. Kwame'}
                />
              </Field>

              <Field id="acc-dob" label="Date of birth" hint="Optional. Used only for your certificate.">
                <input
                  id="acc-dob" type="date" className="afia-input-plain"
                  style={{ colorScheme: 'dark' }} value={details.dob}
                  onChange={(e) => setDetails((d) => ({ ...d, dob: e.target.value }))}
                />
              </Field>

              <Field id="acc-country" label="Country">
                <select
                  id="acc-country" className="afia-input-plain" style={{ colorScheme: 'dark' }}
                  value={details.country}
                  onChange={(e) => setDetails((d) => ({ ...d, country: e.target.value }))}
                >
                  <option value="">Not set</option>
                  {COUNTRY_OPTIONS.map(({ code, name }) => <option key={code} value={code}>{name}</option>)}
                </select>
              </Field>

              <Field id="acc-phone" label="Phone" hint="Optional.">
                <input
                  id="acc-phone" type="tel" inputMode="tel" className="afia-input-plain" value={details.phone}
                  onChange={(e) => setDetails((d) => ({ ...d, phone: e.target.value.replace(/[^0-9+()\-. ]/g, '') }))}
                  autoComplete="tel" maxLength={32}
                />
              </Field>

              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, margin: '4px 0 20px', cursor: 'pointer', color: 'var(--cream-dim, #EDD9BC)', fontSize: 15, lineHeight: 1.5 }}>
                <input
                  type="checkbox" checked={details.marketingOptIn}
                  onChange={(e) => setDetails((d) => ({ ...d, marketingOptIn: e.target.checked }))}
                  style={{ marginTop: 3, width: 16, height: 16, accentColor: '#C9A558', flexShrink: 0 }}
                />
                <span>Email me Mama Africa stories, events and offers. Untick and save to unsubscribe.</span>
              </label>

              {detailsMsg.error && <div className="error-msg" style={{ marginBottom: 16 }}>{detailsMsg.error}</div>}
              {detailsMsg.success && <div className="success-msg" style={{ marginBottom: 16 }}>{detailsMsg.success}</div>}

              <button type="submit" className="btn-gold" disabled={savingDetails}>
                {savingDetails ? 'Saving…' : 'Save changes'}
              </button>
            </form>
          </Panel>

          <div style={{ display: 'grid', gap: 22 }}>
            <Panel title="Password" subtitle="Between 8 and 100 characters.">
              <form onSubmit={savePassword}>
                <PwField id="acc-pw-current" label="Current password"
                  value={pw.current} onChange={(e) => setPw((p) => ({ ...p, current: e.target.value }))} />
                <PwField id="acc-pw-new" label="New password"
                  value={pw.newPw} onChange={(e) => setPw((p) => ({ ...p, newPw: e.target.value }))} />
                <PwField id="acc-pw-confirm" label="Confirm new password"
                  value={pw.confirm} onChange={(e) => setPw((p) => ({ ...p, confirm: e.target.value }))} />

                {pwMsg.error && <div className="error-msg" style={{ marginBottom: 16 }}>{pwMsg.error}</div>}
                {pwMsg.success && <div className="success-msg" style={{ marginBottom: 16 }}>{pwMsg.success}</div>}

                <button type="submit" className="btn-gold" disabled={savingPw}>
                  {savingPw ? 'Updating…' : 'Change password'}
                </button>
              </form>
            </Panel>

            <Panel title="Carry on" subtitle="Where you were headed.">
              <div style={{ display: 'grid', gap: 12 }}>
                {user.dob && (
                  <QuickLink
                    to={`/result?dob=${encodeURIComponent(user.dob)}`}
                    title="Your day-born certificate"
                    blurb="Open and download your certificate."
                  />
                )}
                <QuickLink to="/store" title="The store" blurb="Shirts, mugs and keepsakes for your day." />
                <QuickLink to="/culture" title="Culture hub" blurb="Adinkra, Twi, clans and the calendar." />
                <QuickLink to="/culture" title="Ask Afia" blurb="Questions about Ghana, answered." />
              </div>
            </Panel>
          </div>
        </div>

      </section>
    </div>
  );
}
